import { add, fill, playerColor } from './common';

/**
 * **プレイヤーの鋼の板**（斜めに切った平行四辺形。start の VS と win の勝者の名乗りが共有する。Issue #646 / #604）。
 *
 * 中身: デッキのイラスト（窓いっぱい・斜めに切り抜く）＋ 下の段にプレイヤーのアイコン（小さめの枠）・名前（主）・デッキ名（従）。
 * 外側の縁（P1 は左・P2 は右）にその人の色の太い線（`kit.skewPlateTexture` の `stripe`）。
 * `portrait(p)` はデッキのイラストが無いと本人の絵に落ちるので、`playerIcon(p)` と同じ絵なら窓には出さない
 * （そのときはアイコンを窓の高さで外側に大きく置く）。
 */
export const PLATE = Object.freeze({
  W: 900,          // 板の外接の幅
  H: 450,          // 板の高さ（画面の 4 割）
  SKEW: 120,       // 上の辺のずれ（切り口の傾き）
  WIN: 30,         // 窓の内側への寄せ
  BAND: 170,       // 窓の下の段（アイコン・名前）
  ICON: 168,       // プレイヤーのアイコンの枠
});

/** 同じ絵か（`portrait` はデッキの絵が無いと本人の絵に落ちる） */
export const sameImage = (a, b) => !!a && !!b && (a === b || (!!a.src && a.src === b.src));

/** 平行四辺形（外接の幅 w・高さ h・ずれ skew）の中の、高さ y0〜y1 の帯（同じ傾き）→ { w, h, skew, cx, cy } */
export function subSkew(w, h, skew, y0, y1) {
  const k0 = (y0 + h / 2) / h; const k1 = (y1 + h / 2) / h;
  const left = (k) => -w / 2 + skew * (1 - k);
  const right = (k) => w / 2 - skew * k;
  const x0 = left(k1); const x1 = right(k0);
  return { w: x1 - x0, h: y1 - y0, skew: skew * (k1 - k0), cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
}

/** 金属の文字（銀＋黒の縁）。`maxW` に収まるまで縮めるが、文字は 46px を割らない → `{ m, w, h, s }` */
export function metalText(ctx, text, size, maxW, italic = false) {
  const { THREE, kit } = ctx;
  const t = kit.metalTextTexture(text, { size, edge: null, italic });
  const s = Math.max(46 / size, Math.min(1, maxW / Math.max(1, t.width)));
  const m = new THREE.Mesh(new THREE.PlaneGeometry(t.width, t.height), kit.plain(t.texture, 1));
  return { m, w: t.width * s, h: t.height * s, s };
}

/** 単色の平行四辺形 */
export function tintedSkew(ctx, color, w, h, skew, opacity = 1) {
  const m = ctx.kit.skewQuad(ctx.kit.plain(null, opacity), w, h, skew);
  m.material.color?.set?.(color);
  return m;
}

/** group の中に順番付きで置く（group を root に足してから使う。`add` は子の順番を上書きするため） */
export const putIn = (g, obj, order) => { obj.renderOrder = order; g.add(obj); return obj; };

/** プレイヤーの名前（無ければデッキ名 → P1 / P2） */
export function duelistName(ctx, p) {
  const info = ctx.hud?.players?.[p] ?? {};
  return info.name || info.deckName || `P${p + 1}`;
}

/**
 * 板を 1 枚作って `root` に足す（renderOrder `order`。中身は order + 0.1〜0.7）。返す `g` を動かせば板ごと動く。
 * `outer` は外側の向き（P1 = -1 左 / P2 = +1 右）。
 */
export function duelistPlate(ctx, root, p, { order = 4 } = {}) {
  const { THREE, kit } = ctx;
  const { W: PW, H: PH, SKEW, WIN, BAND, ICON } = PLATE;
  const info = ctx.hud?.players?.[p] ?? {};
  const name0 = duelistName(ctx, p);
  const g = add(root, new THREE.Group(), order);
  const o = (d) => order + d;
  const outer = p === 0 ? -1 : 1;   // 外側の向き
  const color = playerColor(ctx, p);
  const win = subSkew(PW, PH, SKEW, -PH / 2 + WIN + 10, PH / 2 - WIN - 10);
  const band = subSkew(PW, PH, SKEW, PH / 2 - WIN - 10 - BAND, PH / 2 - WIN - 10);
  /** 画像の板（平行四辺形に切り抜く） */
  const imageSkew = (img, w, h, skew) => {
    const tex = kit.imageTexture(img);
    if (!tex) return null;
    const a = (img.naturalWidth || img.width || 1) / (img.naturalHeight || img.height || 1);
    return kit.skewQuad(kit.plain(tex, 1), w, h, skew, { aspect: a });
  };
  putIn(g, kit.quad(kit.plain(kit.skewPlateTexture(PW, PH, { skew: SKEW, stripe: p === 0 ? 'left' : 'right', color, window: WIN }), 1), PW, PH), o(0));
  // デッキのイラスト（窓いっぱい）。本人の絵と同じなら出さない
  const deckImg = ctx.cards?.portrait?.(p) ?? null;
  const iconImg = ctx.cards?.playerIcon?.(p) ?? null;
  const art = sameImage(deckImg, iconImg) ? null : imageSkew(deckImg, win.w - 12, win.h - 12, win.skew * ((win.w - 12) / win.w));
  if (art) { art.position.set(win.cx, win.cy, 0); art.material.color?.set?.('#c8c8c8'); putIn(g, art, o(0.1)); }
  // 下の段（文字の地）
  const bandMesh = tintedSkew(ctx, '#07080b', band.w - 12, band.h, band.skew, 0.82);
  bandMesh.position.set(band.cx, band.cy, 0);
  putIn(g, bandMesh, o(0.2));
  const bandLine = tintedSkew(ctx, '#c9a44c', band.w - band.skew - 12, 3, 0);
  bandLine.position.set(band.cx + band.skew / 2, band.cy - band.h / 2, 0);
  putIn(g, bandLine, o(0.3));
  /*
   * プレイヤーのアイコン（小さめの枠。外側の下の角に、段から少しはみ出して手前に）。
   * デッキのイラストが無い（本人の絵と同じ）ときは窓が空くので、アイコンを窓の高さで外側に大きく置く（1 回だけ出す）
   */
  const iconTex = kit.imageTexture(iconImg);
  const big = !art;
  // 本人の絵も無ければ枠も出さない（文字だけ）
  const isz = !iconTex ? 0 : (big ? win.h - 44 : ICON);
  const iconY = big ? win.cy : PH / 2 - WIN - ICON / 2 - 18;
  const edgeAt = (y) => {
    const k = (y + PH / 2) / PH;
    return outer < 0 ? -PW / 2 + SKEW * (1 - k) : PW / 2 - SKEW * k;
  };
  // 外側の斜めの辺から、アイコンの外側の下の角が離れるように
  const iconX = edgeAt(iconY + isz / 2) - outer * (WIN + 28 + isz / 2);
  if (iconTex) {
    const frame = fill(ctx, color, isz + 12, isz + 12);
    frame.position.set(iconX, iconY, 0);
    const frameBack = fill(ctx, '#0b0c10', isz, isz);
    frameBack.position.set(iconX, iconY, 0);
    putIn(g, frame, o(0.4)); putIn(g, frameBack, o(0.5));
    const im = iconImg;
    const a = (im.naturalWidth || im.width || 1) / (im.naturalHeight || im.height || 1);
    const icon = kit.skewQuad(kit.plain(iconTex, 1), isz - 6, isz - 6, 0, { aspect: a });
    icon.position.set(iconX, iconY, 0);
    putIn(g, icon, o(0.6));
  }
  // 名前（主）とデッキ名（従）。アイコンの内側に寄せて並べる
  const textW = band.w - isz - 150;
  const name = metalText(ctx, name0, 92, textW);
  const deckName = info.deckName && info.deckName !== name0 ? metalText(ctx, info.deckName, 54, textW) : null;
  const textEdge = iconX - outer * (isz / 2 + 26);   // 文字の外側の端（アイコンの内側）
  const nameY = band.cy + (deckName ? 26 : 0);
  name.m.position.set(textEdge - outer * name.w / 2, nameY, 0);
  name.m.scale.set(name.s, name.s, 1);
  putIn(g, name.m, o(0.7));
  if (deckName) {
    deckName.m.position.set(textEdge - outer * deckName.w / 2, band.cy - 48, 0);
    deckName.m.scale.set(deckName.s, deckName.s, 1);
    putIn(g, deckName.m, o(0.7));
  }
  return { g, outer, color };
}
